import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { customAxios } from "lib/axios";

export interface DirectorTeamAttendanceRecord {
  id: string;
  userId: string;
  name: string;
  role: string;
  department: string;
  status: "Present" | "Absent" | "Late" | "Leave";
  punchInTime: string | null;
  punchOutTime: string | null;
  totalWorkingHours: string;
  avatar?: string;
  isHalfDay: boolean;
  isFullDay: boolean;
}

export interface DirectorTeamAttendanceData {
  records: DirectorTeamAttendanceRecord[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalRecords: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
}

export interface DirectorTeamAttendanceResponse {
  success: boolean;
  data: DirectorTeamAttendanceData;
  message?: string;
}

const getDirectorTeamAttendance = async (
  page = 1,
  limit = 10,
  filterType = "Today",
  startDate?: string | null,
  endDate?: string | null
): Promise<DirectorTeamAttendanceResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append("page", page.toString());
  queryParams.append("limit", limit.toString());
  queryParams.append("filterType", filterType);
  if (startDate) queryParams.append("startDate", startDate);
  if (endDate) queryParams.append("endDate", endDate);

  const { data } = await customAxios.get<DirectorTeamAttendanceResponse>(
    `/attendance/director/team/list?${queryParams.toString()}`
  );
  return data;
};

export const useGetDirectorTeamAttendance = (
  page = 1,
  limit = 10,
  filterType = "Today",
  startDate?: string | null,
  endDate?: string | null
) =>
  useQuery<DirectorTeamAttendanceResponse, AxiosError>(
    ["director-team-attendance", page, limit, filterType, startDate, endDate],
    () => getDirectorTeamAttendance(page, limit, filterType, startDate, endDate),
    {
      staleTime: 60000, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );
